import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';
import React, { useEffect } from 'react';
import { THEME_COLORS, DARK_THEME_COLORS, GRADIENTS, SHADOWS, DARK_SHADOWS, BORDER_RADIUS } from './themeConfig';
import { ThemeModeProvider, useThemeMode } from './Thememodecontext';

// Inner component that reads the current theme mode
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDark, colors, shadows } = useThemeMode();

  // ── Imperatively set color-scheme + background on <html> ─────────────────
  // The browser's native date-picker popup is a system-level overlay. It reads
  // `color-scheme` directly from the <html> element's inline style, not from
  // a stylesheet rule. Setting it here via the DOM — and keeping it in sync
  // with isDark — is the only reliable way to make the popup render with a dark
  // background that visually matches your app (#1a1f2e in dark mode).
  // The backgroundColor on <html> also fixes the overscroll area colour.
  useEffect(() => {
    const html = document.documentElement;
    html.style.colorScheme = isDark ? 'dark' : 'light';
    html.style.backgroundColor = isDark
      ? DARK_THEME_COLORS.background.default  // #1a1f2e
      : THEME_COLORS.background.default;      // #f0f2f5
  }, [isDark]);

  // ── Shared Mac-style scrollbar thumb color ────────────────────────────────
  // Semi-transparent so it looks natural on any background color
  const scrollThumb = `${colors.neutral.gray[400]}99`;
  const scrollThumbHover = `${colors.neutral.gray[500]}cc`;

  const globalStyles = (
    <GlobalStyles
      styles={{
        '*:focus-visible': {
          outline: `2px solid ${colors.primary.main}`,
          outlineOffset: '2px',
        },
        '*:focus:not(:focus-visible)': {
          outline: 'none',
        },
        'button:focus, input:focus, textarea:focus, select:focus': {
          outline: 'none',
        },
        'button:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible': {
          outline: `2px solid ${colors.primary.main}`,
          outlineOffset: '2px',
        },
        // Smooth theme transition on the whole page
        '*, *::before, *::after': {
          transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.15s ease',
        },
        // But NOT on transforms/opacity/box-shadow to avoid janky animations
        'button, [role="button"]': {
          transition:
            'background-color 0.25s ease, border-color 0.25s ease, color 0.15s ease, box-shadow 0.2s ease, transform 0.2s ease',
        },

        // ══════════════════════════════════════════════════════════════════════
        // MAC-STYLE OVERLAY SCROLLBARS (all platforms)
        // ──────────────────────────────────────────────────────────────────────
        // On Windows, native scrollbars are wide, always-visible, and push
        // content inward. These rules replicate macOS behaviour:
        //   • 6px wide, hidden by default, fade in on hover
        //   • Painted *on top* of content (overflow: overlay) — no layout shift
        //   • Rounded, semi-transparent thumb on a transparent track
        //
        // `overflow: overlay` is a non-standard but widely-supported Chromium
        // value that keeps the scrollbar from reserving gutter space, exactly
        // as macOS does natively.
        // ══════════════════════════════════════════════════════════════════════

        // Firefox: use scrollbar-color shorthand (no per-state support, so we
        // keep the thumb slightly visible rather than fully hidden)
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `transparent transparent`,
        },
        '*:hover': {
          scrollbarColor: `${scrollThumb} transparent`,
        },

        // Webkit (Chrome, Edge, Safari on Windows/Mac/Linux)
        '::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent',
          borderRadius: '10px',
          transition: 'background-color 0.3s ease',
        },
        // Show thumb when the parent container is hovered
        '*:hover::-webkit-scrollbar-thumb': {
          backgroundColor: scrollThumb,
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: `${scrollThumbHover} !important`,
        },
        '::-webkit-scrollbar-corner': {
          background: 'transparent',
        },

        // Force overlay layout so scrollbar doesn't shift content on Windows
        // @ts-ignore – 'overlay' is non-standard but supported in all Chromium browsers
        'html, body': {
          overflow: 'overlay',
        },

        // Apply overlay overflow to all major scrollable MUI containers so the
        // scrollbar sits on top of content rather than pushing it
        '.MuiDrawer-paper, .MuiDialog-paper, .MuiDialogContent-root, .MuiTableContainer-root, .MuiMenu-paper, .MuiMenu-list, .MuiPopover-paper, .MuiAutocomplete-popper .MuiPaper-root': {
          // @ts-ignore
          overflow: 'overlay',
        },

        // ── Fix: MUI dark mode injects a white `backgroundImage` gradient onto
        // Paper and OutlinedInput elements (elevation overlay). This makes them
        // render as grey instead of your intended paper background colour.
        // ─────────────────────────────────────────────────────────────────────

        '.MuiOutlinedInput-root': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
        },
        '.MuiOutlinedInput-root .MuiSelect-select': {
          backgroundColor: 'transparent !important',
          backgroundImage: 'none !important',
        },
        '.MuiOutlinedInput-input': {
          backgroundColor: 'transparent !important',
        },

        // ── Dropdown popup paper ─
        '.MuiMenu-paper, .MuiPopover-paper, .MuiAutocomplete-popper .MuiPaper-root': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
          border: `1px solid ${colors.neutral.gray[200]}`,
          color: `${colors.neutral.gray[800]} !important`,
        },
        '.MuiMenuItem-root': {
          color: `${colors.neutral.gray[800]} !important`,
        },
        '.MuiMenuItem-root:hover': {
          backgroundColor: `${colors.neutral.gray[100]} !important`,
        },
        '.MuiMenuItem-root.Mui-selected': {
          backgroundColor: `${colors.primary.main}20 !important`,
        },
        '.MuiMenuItem-root.Mui-selected:hover': {
          backgroundColor: `${colors.primary.main}35 !important`,
        },

        // ── FIX 2: Number input spin-button arrows ───────────────────────────
        'input[type="number"]': {
          MozAppearance: 'textfield',
        },
        'input[type="number"]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          appearance: 'none',
          opacity: 1,
          width: '18px',
          cursor: 'pointer',
          backgroundImage: [
            `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 0l5 5H0z' fill='${encodeURIComponent(colors.neutral.gray[500])}'/%3E%3C/svg%3E")`,
            `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 5l5-5H0z' fill='${encodeURIComponent(colors.neutral.gray[500])}'/%3E%3C/svg%3E")`,
          ].join(', '),
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'center 30%, center 70%',
          backgroundSize: '10px 5px, 10px 5px',
          backgroundColor: colors.background.paper,
          borderLeft: `1px solid ${colors.neutral.gray[200]}`,
          borderRadius: '0 6px 6px 0',
          transition: 'background-color 0.15s ease',
        },
        'input[type="number"]::-webkit-inner-spin-button:hover': {
          backgroundColor: colors.neutral.gray[100],
          backgroundImage: [
            `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 0l5 5H0z' fill='${encodeURIComponent(colors.primary.main)}'/%3E%3C/svg%3E")`,
            `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 5l5-5H0z' fill='${encodeURIComponent(colors.primary.main)}'/%3E%3C/svg%3E")`,
          ].join(', '),
          backgroundRepeat: 'no-repeat, no-repeat',
          backgroundPosition: 'center 30%, center 70%',
          backgroundSize: '10px 5px, 10px 5px',
        },
        'input[type="number"]::-webkit-outer-spin-button': {
          WebkitAppearance: 'none',
          appearance: 'none',
          margin: 0,
        },

        // ══════════════════════════════════════════════════════════════════════
        // FIX: Native date input — calendar icon & popup color scheme
        // ──────────────────────────────────────────────────────────────────────
        // type="date" uses the browser's native widget. CSS `color-scheme` on
        // the INPUT itself tells the browser which colour palette to use for
        // BOTH the text inside the field AND the popup calendar it spawns.
        //
        // Setting it on :root / html as well ensures the popup (which inherits
        // from the document root in Chromium) also respects the chosen scheme.
        //
        // For the calendar icon we REPLACE the browser-drawn system icon
        // entirely by setting the pseudo-element's appearance to none and
        // painting our own SVG via background-image. This is the only reliable
        // cross-Chromium way to colour it — CSS filter on the native icon is
        // ignored in many versions when the icon is rendered at the OS level.
        // ══════════════════════════════════════════════════════════════════════

        // ── 1. Propagate color-scheme to the document root ───────────────────
        // The native calendar popup is a browser-level overlay that reads
        // color-scheme from the root element. Setting it here (in addition to
        // on each input) ensures the popup background is dark in dark mode.
        ':root': {
          colorScheme: isDark ? 'dark' : 'light',
        },

        // ── 2. Set color-scheme on every date/time input ──────────────────────
        'input[type="date"], input[type="datetime-local"], input[type="time"], input[type="month"], input[type="week"]':
          {
            colorScheme: isDark ? 'dark' : 'light',
            color: `${colors.neutral.gray[800]} !important`,
          },

        // ── 3. Replace the native calendar/clock icon with a custom SVG ───────
        // We encode the primary colour directly into the SVG fill so the icon
        // always matches the theme — no filter math required.
        // The SVG is a standard calendar outline that matches the MUI aesthetic.
        'input[type="date"]::-webkit-calendar-picker-indicator, input[type="datetime-local"]::-webkit-calendar-picker-indicator, input[type="time"]::-webkit-calendar-picker-indicator, input[type="month"]::-webkit-calendar-picker-indicator, input[type="week"]::-webkit-calendar-picker-indicator':
          {
            // Hide the default OS-drawn icon
            WebkitAppearance: 'none',
            appearance: 'none',
            // Replace with our SVG — colour encodes the theme primary
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: '16px 16px',
            // Give it a fixed size so it doesn't collapse
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            borderRadius: '4px',
            padding: '2px',
            opacity: 0.85,
            transition: 'opacity 0.2s ease, background-color 0.2s ease',
          },

        'input[type="date"]::-webkit-calendar-picker-indicator:hover, input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover, input[type="time"]::-webkit-calendar-picker-indicator:hover, input[type="month"]::-webkit-calendar-picker-indicator:hover, input[type="week"]::-webkit-calendar-picker-indicator:hover':
          {
            opacity: 1,
            backgroundColor: `${colors.primary.main}20`,
          },

        // ── 4. Inner date segments (day / month / year text) ──────────────────
        'input[type="date"]::-webkit-datetime-edit, input[type="datetime-local"]::-webkit-datetime-edit':
          {
            color: colors.neutral.gray[800],
          },
        'input[type="date"]::-webkit-datetime-edit-fields-wrapper, input[type="datetime-local"]::-webkit-datetime-edit-fields-wrapper':
          {
            backgroundColor: 'transparent',
          },
        'input[type="date"]::-webkit-datetime-edit-year-field:focus, input[type="date"]::-webkit-datetime-edit-month-field:focus, input[type="date"]::-webkit-datetime-edit-day-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-year-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-month-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-day-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-hour-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-minute-field:focus':
          {
            backgroundColor: colors.primary.main,
            color: '#ffffff',
            borderRadius: '2px',
            outline: 'none',
          },

        // ── FIX 2b: Date picker calendar icon color in dark mode ─────────────
        '.MuiInputAdornment-root .MuiIconButton-root': {
          color: `${colors.neutral.gray[500]} !important`,
        },
        '.MuiInputAdornment-root .MuiIconButton-root:hover': {
          color: `${colors.primary.main} !important`,
          backgroundColor: `${colors.primary.main}14 !important`,
        },
        // Date/Time picker dialog & desktop popper (MUI X DatePicker)
        '.MuiPickersPopper-paper, .MuiDateCalendar-root, .MuiPickersLayout-root': {
          backgroundColor: `${colors.background.paper} !important`,
          backgroundImage: 'none !important',
          color: `${colors.neutral.gray[800]} !important`,
        },
        '.MuiPickersDay-root': {
          color: `${colors.neutral.gray[800]} !important`,
          backgroundColor: 'transparent !important',
        },
        '.MuiPickersDay-root:hover': {
          backgroundColor: `${colors.neutral.gray[100]} !important`,
        },
        '.MuiPickersDay-root.Mui-selected': {
          backgroundColor: `${colors.primary.main} !important`,
          color: '#ffffff !important',
        },
        '.MuiDayCalendar-weekDayLabel': {
          color: `${colors.neutral.gray[500]} !important`,
        },
        '.MuiPickersCalendarHeader-label, .MuiPickersCalendarHeader-switchViewButton': {
          color: `${colors.neutral.gray[800]} !important`,
        },
        '.MuiPickersArrowSwitcher-button': {
          color: `${colors.neutral.gray[500]} !important`,
        },
        '.MuiPickersArrowSwitcher-button:hover': {
          color: `${colors.primary.main} !important`,
        },
      }}
    />
  );

  const theme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
        contrastText: '#ffffff',
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
        contrastText: '#ffffff',
      },
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: {
        primary: colors.neutral.gray[800],
        secondary: colors.neutral.gray[500],
      },
      grey: colors.neutral.gray as any,
      success: { main: colors.status.success },
      warning: { main: colors.status.warning },
      error: { main: colors.status.error },
      info: { main: colors.status.info },
    },

    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 600 },
      h4: { fontWeight: 600 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      body1: { fontSize: '0.95rem', lineHeight: 1.6 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
    },

    shape: {
      borderRadius: parseInt(BORDER_RADIUS.md),
    },

    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.md,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.95rem',
            padding: '10px 24px',
            boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: shadows.primary,
              transform: 'translateY(-1px)',
            },
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
          },
          contained: {
            backgroundColor: colors.primary.main,
            color: THEME_COLORS.neutral.white,
            '&:hover': { backgroundColor: colors.primary.dark },
            '&:disabled': {
              background: colors.neutral.gray[300],
              color: colors.neutral.gray[500],
            },
          },
          outlined: {
            borderColor: colors.neutral.gray[200],
            color: colors.neutral.gray[600],
            backgroundColor: colors.background.paper,
            '&:hover': {
              backgroundColor: colors.neutral.gray[50],
              borderColor: colors.primary.main,
            },
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.lg,
            border: `1px solid ${colors.neutral.gray[100]}`,
            boxShadow: shadows.md,
            backgroundColor: colors.background.paper,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: shadows.lg,
              transform: 'translateY(-2px)',
            },
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.md,
            boxShadow: shadows.md,
            backgroundColor: colors.background.paper,
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
          },
          elevation0: {
            boxShadow: 'none',
            border: `1px solid ${colors.neutral.gray[200]}`,
          },
          elevation1: { boxShadow: shadows.sm },
          elevation3: { boxShadow: shadows.md },
          elevation8: { boxShadow: shadows.xl },
        },
      },

      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: BORDER_RADIUS.sm,
              backgroundColor: colors.background.paper,
              transition: 'all 0.2s ease-in-out',
              '& fieldset': { borderColor: colors.neutral.gray[200] },
              '&:hover fieldset': { borderColor: colors.primary.main },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary.main,
                borderWidth: 2,
              },
              '&:focus': { outline: 'none' },
            },
            '& .MuiOutlinedInput-root.Mui-focused': { outline: 'none' },
            '& input:focus': { outline: 'none' },

            // ── Native date/time input inside MUI TextField ──────────────────
            // Ensure the color-scheme propagates even when the input is nested
            // inside MUI's OutlinedInput wrapper.
            '& input[type="date"], & input[type="datetime-local"], & input[type="time"], & input[type="month"], & input[type="week"]':
              {
                colorScheme: isDark ? 'dark' : 'light',
                color: colors.neutral.gray[800],
              },

            // ── Custom SVG calendar icon (replaces OS-drawn icon via appearance:none)
            '& input[type="date"]::-webkit-calendar-picker-indicator, & input[type="datetime-local"]::-webkit-calendar-picker-indicator, & input[type="time"]::-webkit-calendar-picker-indicator, & input[type="month"]::-webkit-calendar-picker-indicator, & input[type="week"]::-webkit-calendar-picker-indicator':
              {
                WebkitAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: '16px 16px',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                borderRadius: '4px',
                padding: '2px',
                opacity: 0.85,
                transition: 'opacity 0.2s ease, background-color 0.2s ease',
              },
            '& input[type="date"]::-webkit-calendar-picker-indicator:hover, & input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover, & input[type="time"]::-webkit-calendar-picker-indicator:hover, & input[type="month"]::-webkit-calendar-picker-indicator:hover, & input[type="week"]::-webkit-calendar-picker-indicator:hover':
              {
                opacity: 1,
                backgroundColor: `${colors.primary.main}20`,
              },
            // Focused date segments styled to primary colour
            '& input[type="date"]::-webkit-datetime-edit-year-field:focus, & input[type="date"]::-webkit-datetime-edit-month-field:focus, & input[type="date"]::-webkit-datetime-edit-day-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-year-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-month-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-day-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-hour-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-minute-field:focus':
              {
                backgroundColor: colors.primary.main,
                color: '#ffffff',
                borderRadius: '2px',
                outline: 'none',
              },
          },
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            // ── FIX 3: Number input focus loss ─────────────────────────────
            transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:focus': { outline: 'none' },
            '&.Mui-focused': { outline: 'none' },
          },
          input: {
            transition: 'none',
            '&:focus': { outline: 'none' },

            // ── Prevent mobile Safari/Chrome auto-zoom on focus ───────────────
            // Browsers zoom the viewport when a focused input's font-size is
            // under 16px — MUI's size="small" inputs default to 14px, so force
            // 16px specifically below the sm breakpoint (desktop sizing untouched).
            '@media (max-width:600px)': { fontSize: '16px' },

            // ── Native date input color-scheme on MuiInputBase level ─────────
            // Catches cases where type="date" inputs are rendered directly
            // inside InputBase without going through MuiTextField.
            '&[type="date"], &[type="datetime-local"], &[type="time"], &[type="month"], &[type="week"]':
              {
                colorScheme: isDark ? 'dark' : 'light',
                color: colors.neutral.gray[800],
              },
            '&[type="date"]::-webkit-calendar-picker-indicator, &[type="datetime-local"]::-webkit-calendar-picker-indicator, &[type="time"]::-webkit-calendar-picker-indicator, &[type="month"]::-webkit-calendar-picker-indicator, &[type="week"]::-webkit-calendar-picker-indicator':
              {
                WebkitAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: '16px 16px',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                borderRadius: '4px',
                padding: '2px',
                opacity: 0.85,
                transition: 'opacity 0.2s ease, background-color 0.2s ease',
              },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            backgroundColor: colors.background.paper,
            backgroundImage: 'none',
            transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:focus': { outline: 'none' },
            '&.Mui-focused': {
              outline: 'none',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary.main,
                borderWidth: 2,
              },
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
            },
          },
          notchedOutline: {
            borderColor: colors.neutral.gray[200],
          },
          input: {
            backgroundColor: 'transparent',
            backgroundImage: 'none',
            transition: 'none',
            '&:focus': { outline: 'none' },

            // ── Prevent mobile Safari/Chrome auto-zoom on focus (see MuiInputBase) ──
            '@media (max-width:600px)': { fontSize: '16px' },

            // ── Native date input color-scheme on OutlinedInput level ────────
            '&[type="date"], &[type="datetime-local"], &[type="time"], &[type="month"], &[type="week"]':
              {
                colorScheme: isDark ? 'dark' : 'light',
                color: colors.neutral.gray[800],
              },
            '&[type="date"]::-webkit-calendar-picker-indicator, &[type="datetime-local"]::-webkit-calendar-picker-indicator, &[type="time"]::-webkit-calendar-picker-indicator, &[type="month"]::-webkit-calendar-picker-indicator, &[type="week"]::-webkit-calendar-picker-indicator':
              {
                WebkitAppearance: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: '16px 16px',
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                borderRadius: '4px',
                padding: '2px',
                opacity: 0.85,
                transition: 'opacity 0.2s ease, background-color 0.2s ease',
              },
          },
        },
      },

      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            '&:focus': { outline: 'none' },
          },
          select: {
            backgroundColor: 'transparent !important',
            '&:focus': {
              backgroundColor: 'transparent',
              outline: 'none',
            },
            // ── Prevent mobile Safari/Chrome auto-zoom on focus (see MuiInputBase) ──
            '@media (max-width:600px)': { fontSize: '16px' },
          },
          icon: {
            color: colors.neutral.gray[500],
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: colors.neutral.gray[500],
            '&.Mui-focused': {
              color: colors.primary.main,
            },
          },
        },
      },

      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: colors.neutral.gray[500],
            '& .MuiSvgIcon-root': {
              color: colors.neutral.gray[500],
            },
            '& .MuiIconButton-root': {
              color: colors.neutral.gray[500],
              '&:hover': {
                color: colors.primary.main,
                backgroundColor: `${colors.primary.main}14`,
              },
            },
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background.paper,
            backgroundImage: 'none',
            border: `1px solid ${colors.neutral.gray[200]}`,
            boxShadow: shadows.lg,
            borderRadius: BORDER_RADIUS.sm,
            // Mac-style overlay scrollbar — no per-component overrides needed;
            // the global * rule covers this. We only set overflow here.
            // @ts-ignore
            overflow: 'overlay',
          },
          list: {
            padding: '4px',
            // @ts-ignore
            overflow: 'overlay',
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            margin: '2px 0',
            color: colors.neutral.gray[800],
            fontSize: '0.9rem',
            transition: 'background-color 0.15s ease',
            '&:focus': {
              outline: 'none',
              backgroundColor: colors.neutral.gray[50],
            },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '-2px',
            },
            '&:hover': {
              backgroundColor: colors.neutral.gray[100],
            },
            '&.Mui-selected': {
              backgroundColor: `${colors.primary.main}20`,
              color: colors.primary.main,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: `${colors.primary.main}35`,
              },
            },
          },
        },
      },

      MuiTableContainer: {
        styleOverrides: {
          root: {
            // Mac-style overlay scrollbar via global rule; just set overflow mode
            // @ts-ignore
            overflow: 'overlay',
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.sm,
            fontWeight: 600,
            fontSize: '0.8rem',
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
          },
          outlined: {
            '&.MuiChip-colorWarning': {
              backgroundColor: isDark ? 'rgba(255,167,38,0.22)'  : 'rgba(255,167,38,0.12)',
              borderColor:     isDark ? 'rgba(255,167,38,0.55)'  : 'rgba(255,167,38,0.40)',
              color:           isDark ? '#ffcc80'                 : '#b76e00',
              '& .MuiChip-icon':  { color: isDark ? '#ffa726' : '#e68900' },
              '& .MuiChip-label': { color: isDark ? '#ffcc80' : '#b76e00' },
            },
            '&.MuiChip-colorSuccess': {
              backgroundColor: isDark ? 'rgba(102,187,106,0.22)' : 'rgba(102,187,106,0.15)',
              borderColor:     isDark ? 'rgba(102,187,106,0.50)' : 'rgba(102,187,106,0.45)',
              color:           isDark ? '#a5d6a7'                 : '#2e7d32',
              '& .MuiChip-icon':  { color: isDark ? '#66bb6a' : '#43a047' },
              '& .MuiChip-label': { color: isDark ? '#a5d6a7' : '#2e7d32' },
            },
            '&.MuiChip-colorError': {
              backgroundColor: isDark ? 'rgba(239,83,80,0.22)'   : 'rgba(239,83,80,0.12)',
              borderColor:     isDark ? 'rgba(239,83,80,0.50)'   : 'rgba(239,83,80,0.40)',
              color:           isDark ? '#ef9a9a'                 : '#c62828',
              '& .MuiChip-icon':  { color: isDark ? '#ef5350' : '#e53935' },
              '& .MuiChip-label': { color: isDark ? '#ef9a9a' : '#c62828' },
            },
            '&.MuiChip-colorInfo': {
              backgroundColor: isDark ? 'rgba(41,182,246,0.20)'  : 'rgba(41,182,246,0.12)',
              borderColor:     isDark ? 'rgba(41,182,246,0.50)'  : 'rgba(41,182,246,0.40)',
              color:           isDark ? '#81d4fa'                 : '#0277bd',
              '& .MuiChip-icon':  { color: isDark ? '#29b6f6' : '#039be5' },
              '& .MuiChip-label': { color: isDark ? '#81d4fa' : '#0277bd' },
            },
            '&.MuiChip-colorPrimary': {
              backgroundColor: isDark ? 'rgba(33,150,243,0.22)'  : 'rgba(33,150,243,0.12)',
              borderColor:     isDark ? 'rgba(33,150,243,0.50)'  : 'rgba(33,150,243,0.40)',
              color:           isDark ? '#90caf9'                 : '#1565c0',
              '& .MuiChip-icon':  { color: isDark ? '#42a5f5' : '#1976d2' },
              '& .MuiChip-label': { color: isDark ? '#90caf9' : '#1565c0' },
            },
            '&.MuiChip-colorSecondary': {
              backgroundColor: isDark ? 'rgba(171,71,188,0.22)'  : 'rgba(171,71,188,0.10)',
              borderColor:     isDark ? 'rgba(171,71,188,0.50)'  : 'rgba(171,71,188,0.35)',
              color:           isDark ? '#ce93d8'                 : '#6a1b9a',
              '& .MuiChip-icon':  { color: isDark ? '#ab47bc' : '#8e24aa' },
              '& .MuiChip-label': { color: isDark ? '#ce93d8' : '#6a1b9a' },
            },
          },
          colorPrimary: {
            backgroundColor: colors.primary.main,
            color: '#ffffff',
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS.md,
            margin: '2px 0',
            transition: 'all 0.2s ease-in-out',
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
            '&.Mui-selected': {
              backgroundColor: colors.primary.main,
              color: THEME_COLORS.neutral.white,
              '&:hover': { backgroundColor: colors.primary.dark },
              '& .MuiListItemIcon-root': { color: THEME_COLORS.neutral.white },
              '& .MuiListItemText-primary': { color: THEME_COLORS.neutral.white },
            },
            '&:hover': { backgroundColor: colors.neutral.gray[50] },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            color: colors.neutral.gray[500],
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.paper,
            color: colors.neutral.gray[800],
            boxShadow: shadows.sm,
            borderBottom: `1px solid ${colors.neutral.gray[100]}`,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background.default,
            backgroundImage: 'none',
            borderRight: `1px solid ${colors.neutral.gray[100]}`,
            boxShadow: shadows.lg,
            // Mac-style overlay scrollbar via global rule; just set overflow mode
            // @ts-ignore
            overflow: 'overlay',
            '&:focus': { outline: 'none' },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background.paper,
            backgroundImage: 'none',
            // Mac-style overlay scrollbar via global rule; just set overflow mode
            // @ts-ignore
            overflow: 'overlay',
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            // Mac-style overlay scrollbar via global rule; just set overflow mode
            // @ts-ignore
            overflow: 'overlay',
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              backgroundColor: colors.primary.main,
              color: THEME_COLORS.neutral.white,
              fontWeight: 600,
              fontSize: '0.9rem',
            },
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: colors.neutral.gray[50] },
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '-2px',
            },
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: BORDER_RADIUS.md },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: '4px',
            height: 8,
            backgroundColor: colors.neutral.gray[200],
          },
          bar: { backgroundColor: colors.primary.main },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '4px',
            },
            '&.Mui-checked': { color: colors.primary.main },
          },
        },
      },

      MuiRadio: {
        styleOverrides: {
          root: {
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
              borderRadius: '50%',
            },
            '&.Mui-checked': { color: colors.primary.main },
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: {
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
              borderRadius: BORDER_RADIUS.full,
            },
          },
          switchBase: {
            '&.Mui-checked': {
              color: colors.primary.main,
              '& + .MuiSwitch-track': {
                backgroundColor: colors.primary.main,
              },
            },
          },
        },
      },

      MuiSlider: {
        styleOverrides: {
          root: {
            color: colors.primary.main,
            '& .MuiSlider-thumb:focus': { outline: 'none' },
            '& .MuiSlider-thumb:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            '&:focus': { outline: 'none' },
            '&:focus-visible': {
              outline: `2px solid ${colors.primary.main}`,
              outlineOffset: '2px',
            },
            '&.Mui-selected': { color: colors.primary.main },
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: colors.primary.main },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.neutral.gray[100],
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? colors.neutral.gray[200] : colors.neutral.gray[800],
            color: isDark ? colors.neutral.gray[800] : colors.neutral.white,
            fontSize: '0.75rem',
            borderRadius: BORDER_RADIUS.sm,
          },
          arrow: {
            color: isDark ? colors.neutral.gray[200] : colors.neutral.gray[800],
          },
        },
      },
    },
  });

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      {children}
    </MuiThemeProvider>
  );
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const AppThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <ThemeModeProvider>
      <ThemedApp>{children}</ThemedApp>
    </ThemeModeProvider>
  );
};

// Export theme for use in components (light version as default)
export const appTheme = createTheme({
  palette: {
    primary: {
      main: THEME_COLORS.primary.main,
      light: THEME_COLORS.primary.light,
      dark: THEME_COLORS.primary.dark,
      contrastText: '#ffffff',
    },
  },
});

export default AppThemeProvider;


// import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
// import { CssBaseline, GlobalStyles } from '@mui/material';
// import React, { useEffect } from 'react';
// import { THEME_COLORS, DARK_THEME_COLORS, GRADIENTS, SHADOWS, DARK_SHADOWS, BORDER_RADIUS } from './themeConfig';
// import { ThemeModeProvider, useThemeMode } from './Thememodecontext';

// // Inner component that reads the current theme mode
// const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
//   const { isDark, colors, shadows } = useThemeMode();

//   // ── Imperatively set color-scheme + background on <html> ─────────────────
//   // The browser's native date-picker popup is a system-level overlay. It reads
//   // `color-scheme` directly from the <html> element's inline style, not from
//   // a stylesheet rule. Setting it here via the DOM — and keeping it in sync
//   // with isDark — is the only reliable way to make the popup render with a dark
//   // background that visually matches your app (#1a1f2e in dark mode).
//   // The backgroundColor on <html> also fixes the overscroll area colour.
//   useEffect(() => {
//     const html = document.documentElement;
//     html.style.colorScheme = isDark ? 'dark' : 'light';
//     html.style.backgroundColor = isDark
//       ? DARK_THEME_COLORS.background.default  // #1a1f2e
//       : THEME_COLORS.background.default;      // #f0f2f5
//   }, [isDark]);

//   const globalStyles = (
//     <GlobalStyles
//       styles={{
//         '*:focus-visible': {
//           outline: `2px solid ${colors.primary.main}`,
//           outlineOffset: '2px',
//         },
//         '*:focus:not(:focus-visible)': {
//           outline: 'none',
//         },
//         'button:focus, input:focus, textarea:focus, select:focus': {
//           outline: 'none',
//         },
//         'button:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible': {
//           outline: `2px solid ${colors.primary.main}`,
//           outlineOffset: '2px',
//         },
//         // Smooth theme transition on the whole page
//         '*, *::before, *::after': {
//           transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.15s ease',
//         },
//         // But NOT on transforms/opacity/box-shadow to avoid janky animations
//         'button, [role="button"]': {
//           transition:
//             'background-color 0.25s ease, border-color 0.25s ease, color 0.15s ease, box-shadow 0.2s ease, transform 0.2s ease',
//         },

//         // ── Fix: MUI dark mode injects a white `backgroundImage` gradient onto
//         // Paper and OutlinedInput elements (elevation overlay). This makes them
//         // render as grey instead of your intended paper background colour.
//         // ─────────────────────────────────────────────────────────────────────

//         '.MuiOutlinedInput-root': {
//           backgroundColor: `${colors.background.paper} !important`,
//           backgroundImage: 'none !important',
//         },
//         '.MuiOutlinedInput-root .MuiSelect-select': {
//           backgroundColor: 'transparent !important',
//           backgroundImage: 'none !important',
//         },
//         '.MuiOutlinedInput-input': {
//           backgroundColor: 'transparent !important',
//         },

//         // ── Dropdown popup paper ─
//         '.MuiMenu-paper, .MuiPopover-paper, .MuiAutocomplete-popper .MuiPaper-root': {
//           backgroundColor: `${colors.background.paper} !important`,
//           backgroundImage: 'none !important',
//           border: `1px solid ${colors.neutral.gray[200]}`,
//           color: `${colors.neutral.gray[800]} !important`,
//         },
//         '.MuiMenuItem-root': {
//           color: `${colors.neutral.gray[800]} !important`,
//         },
//         '.MuiMenuItem-root:hover': {
//           backgroundColor: `${colors.neutral.gray[100]} !important`,
//         },
//         '.MuiMenuItem-root.Mui-selected': {
//           backgroundColor: `${colors.primary.main}20 !important`,
//         },
//         '.MuiMenuItem-root.Mui-selected:hover': {
//           backgroundColor: `${colors.primary.main}35 !important`,
//         },

//         // ── FIX: Dropdown/Menu/Popover scrollbar dark mode support ───────────
//         '.MuiMenu-paper, .MuiPopover-paper, .MuiAutocomplete-popper .MuiPaper-root, .MuiMenu-list': {
//           scrollbarWidth: 'thin',
//           scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//         },
//         '.MuiMenu-paper::-webkit-scrollbar, .MuiPopover-paper::-webkit-scrollbar, .MuiAutocomplete-popper .MuiPaper-root::-webkit-scrollbar': {
//           width: '8px',
//           height: '8px',
//         },
//         '.MuiMenu-paper::-webkit-scrollbar-track, .MuiPopover-paper::-webkit-scrollbar-track, .MuiAutocomplete-popper .MuiPaper-root::-webkit-scrollbar-track': {
//           background: colors.background.paper,
//           borderRadius: '4px',
//         },
//         '.MuiMenu-paper::-webkit-scrollbar-thumb, .MuiPopover-paper::-webkit-scrollbar-thumb, .MuiAutocomplete-popper .MuiPaper-root::-webkit-scrollbar-thumb': {
//           backgroundColor: colors.neutral.gray[400],
//           borderRadius: '4px',
//           border: `2px solid ${colors.background.paper}`,
//         },
//         '.MuiMenu-paper::-webkit-scrollbar-thumb:hover, .MuiPopover-paper::-webkit-scrollbar-thumb:hover, .MuiAutocomplete-popper .MuiPaper-root::-webkit-scrollbar-thumb:hover': {
//           backgroundColor: colors.neutral.gray[500],
//         },
//         '.MuiMenu-paper::-webkit-scrollbar-corner, .MuiPopover-paper::-webkit-scrollbar-corner, .MuiAutocomplete-popper .MuiPaper-root::-webkit-scrollbar-corner': {
//           background: colors.background.paper,
//         },

//         // ── Scrollbar styling for MuiTableContainer ─
//         '.MuiTableContainer-root': {
//           scrollbarWidth: 'thin',
//           scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//         },
//         '.MuiTableContainer-root::-webkit-scrollbar': {
//           width: '8px',
//           height: '8px',
//         },
//         '.MuiTableContainer-root::-webkit-scrollbar-track': {
//           background: colors.background.paper,
//           borderRadius: '4px',
//         },
//         '.MuiTableContainer-root::-webkit-scrollbar-thumb': {
//           backgroundColor: colors.neutral.gray[400],
//           borderRadius: '4px',
//           border: `2px solid ${colors.background.paper}`,
//         },
//         '.MuiTableContainer-root::-webkit-scrollbar-thumb:hover': {
//           backgroundColor: colors.neutral.gray[500],
//         },
//         '.MuiTableContainer-root::-webkit-scrollbar-corner': {
//           background: colors.background.paper,
//         },

//         // ── FIX 1: Drawer scrollbar styling ─────────────────────────────────
//         '.MuiDrawer-paper': {
//           scrollbarWidth: 'thin',
//           scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.default}`,
//         },
//         '.MuiDrawer-paper::-webkit-scrollbar': {
//           width: '8px',
//           height: '8px',
//         },
//         '.MuiDrawer-paper::-webkit-scrollbar-track': {
//           background: colors.background.default,
//           borderRadius: '4px',
//         },
//         '.MuiDrawer-paper::-webkit-scrollbar-thumb': {
//           backgroundColor: colors.neutral.gray[400],
//           borderRadius: '4px',
//           border: `2px solid ${colors.background.default}`,
//         },
//         '.MuiDrawer-paper::-webkit-scrollbar-thumb:hover': {
//           backgroundColor: colors.neutral.gray[500],
//         },
//         '.MuiDrawer-paper::-webkit-scrollbar-corner': {
//           background: colors.background.default,
//         },

//         // ── FIX: Dialog & DialogContent scrollbar dark mode support ──────────
//         // The MuiDialog-paper is the modal container; MuiDialogContent-root is
//         // the scrollable inner area. Both need themed scrollbars so the
//         // scrollbar track/thumb match the dialog background in dark mode.
//         '.MuiDialog-paper, .MuiDialogContent-root': {
//           scrollbarWidth: 'thin',
//           scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//         },
//         '.MuiDialog-paper::-webkit-scrollbar, .MuiDialogContent-root::-webkit-scrollbar': {
//           width: '8px',
//           height: '8px',
//         },
//         '.MuiDialog-paper::-webkit-scrollbar-track, .MuiDialogContent-root::-webkit-scrollbar-track': {
//           background: colors.background.paper,
//           borderRadius: '4px',
//         },
//         '.MuiDialog-paper::-webkit-scrollbar-thumb, .MuiDialogContent-root::-webkit-scrollbar-thumb': {
//           backgroundColor: colors.neutral.gray[400],
//           borderRadius: '4px',
//           border: `2px solid ${colors.background.paper}`,
//         },
//         '.MuiDialog-paper::-webkit-scrollbar-thumb:hover, .MuiDialogContent-root::-webkit-scrollbar-thumb:hover': {
//           backgroundColor: colors.neutral.gray[500],
//         },
//         '.MuiDialog-paper::-webkit-scrollbar-corner, .MuiDialogContent-root::-webkit-scrollbar-corner': {
//           background: colors.background.paper,
//         },

//         // ── FIX 2: Number input spin-button arrows ───────────────────────────
//         'input[type="number"]': {
//           MozAppearance: 'textfield',
//         },
//         'input[type="number"]::-webkit-inner-spin-button': {
//           WebkitAppearance: 'none',
//           appearance: 'none',
//           opacity: 1,
//           width: '18px',
//           cursor: 'pointer',
//           backgroundImage: [
//             `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 0l5 5H0z' fill='${encodeURIComponent(colors.neutral.gray[500])}'/%3E%3C/svg%3E")`,
//             `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 5l5-5H0z' fill='${encodeURIComponent(colors.neutral.gray[500])}'/%3E%3C/svg%3E")`,
//           ].join(', '),
//           backgroundRepeat: 'no-repeat, no-repeat',
//           backgroundPosition: 'center 30%, center 70%',
//           backgroundSize: '10px 5px, 10px 5px',
//           backgroundColor: colors.background.paper,
//           borderLeft: `1px solid ${colors.neutral.gray[200]}`,
//           borderRadius: '0 6px 6px 0',
//           transition: 'background-color 0.15s ease',
//         },
//         'input[type="number"]::-webkit-inner-spin-button:hover': {
//           backgroundColor: colors.neutral.gray[100],
//           backgroundImage: [
//             `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 0l5 5H0z' fill='${encodeURIComponent(colors.primary.main)}'/%3E%3C/svg%3E")`,
//             `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='5'%3E%3Cpath d='M5 5l5-5H0z' fill='${encodeURIComponent(colors.primary.main)}'/%3E%3C/svg%3E")`,
//           ].join(', '),
//           backgroundRepeat: 'no-repeat, no-repeat',
//           backgroundPosition: 'center 30%, center 70%',
//           backgroundSize: '10px 5px, 10px 5px',
//         },
//         'input[type="number"]::-webkit-outer-spin-button': {
//           WebkitAppearance: 'none',
//           appearance: 'none',
//           margin: 0,
//         },

//         // ══════════════════════════════════════════════════════════════════════
//         // FIX: Native date input — calendar icon & popup color scheme
//         // ──────────────────────────────────────────────────────────────────────
//         // type="date" uses the browser's native widget. CSS `color-scheme` on
//         // the INPUT itself tells the browser which colour palette to use for
//         // BOTH the text inside the field AND the popup calendar it spawns.
//         //
//         // Setting it on :root / html as well ensures the popup (which inherits
//         // from the document root in Chromium) also respects the chosen scheme.
//         //
//         // For the calendar icon we REPLACE the browser-drawn system icon
//         // entirely by setting the pseudo-element's appearance to none and
//         // painting our own SVG via background-image. This is the only reliable
//         // cross-Chromium way to colour it — CSS filter on the native icon is
//         // ignored in many versions when the icon is rendered at the OS level.
//         // ══════════════════════════════════════════════════════════════════════

//         // ── 1. Propagate color-scheme to the document root ───────────────────
//         // The native calendar popup is a browser-level overlay that reads
//         // color-scheme from the root element. Setting it here (in addition to
//         // on each input) ensures the popup background is dark in dark mode.
//         ':root': {
//           colorScheme: isDark ? 'dark' : 'light',
//         },

//         // ── 2. Set color-scheme on every date/time input ──────────────────────
//         'input[type="date"], input[type="datetime-local"], input[type="time"], input[type="month"], input[type="week"]':
//           {
//             colorScheme: isDark ? 'dark' : 'light',
//             color: `${colors.neutral.gray[800]} !important`,
//           },

//         // ── 3. Replace the native calendar/clock icon with a custom SVG ───────
//         // We encode the primary colour directly into the SVG fill so the icon
//         // always matches the theme — no filter math required.
//         // The SVG is a standard calendar outline that matches the MUI aesthetic.
//         'input[type="date"]::-webkit-calendar-picker-indicator, input[type="datetime-local"]::-webkit-calendar-picker-indicator, input[type="time"]::-webkit-calendar-picker-indicator, input[type="month"]::-webkit-calendar-picker-indicator, input[type="week"]::-webkit-calendar-picker-indicator':
//           {
//             // Hide the default OS-drawn icon
//             WebkitAppearance: 'none',
//             appearance: 'none',
//             // Replace with our SVG — colour encodes the theme primary
//             backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
//             backgroundRepeat: 'no-repeat',
//             backgroundPosition: 'center',
//             backgroundSize: '16px 16px',
//             // Give it a fixed size so it doesn't collapse
//             width: '20px',
//             height: '20px',
//             cursor: 'pointer',
//             borderRadius: '4px',
//             padding: '2px',
//             opacity: 0.85,
//             transition: 'opacity 0.2s ease, background-color 0.2s ease',
//           },

//         'input[type="date"]::-webkit-calendar-picker-indicator:hover, input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover, input[type="time"]::-webkit-calendar-picker-indicator:hover, input[type="month"]::-webkit-calendar-picker-indicator:hover, input[type="week"]::-webkit-calendar-picker-indicator:hover':
//           {
//             opacity: 1,
//             backgroundColor: `${colors.primary.main}20`,
//           },

//         // ── 4. Inner date segments (day / month / year text) ──────────────────
//         'input[type="date"]::-webkit-datetime-edit, input[type="datetime-local"]::-webkit-datetime-edit':
//           {
//             color: colors.neutral.gray[800],
//           },
//         'input[type="date"]::-webkit-datetime-edit-fields-wrapper, input[type="datetime-local"]::-webkit-datetime-edit-fields-wrapper':
//           {
//             backgroundColor: 'transparent',
//           },
//         'input[type="date"]::-webkit-datetime-edit-year-field:focus, input[type="date"]::-webkit-datetime-edit-month-field:focus, input[type="date"]::-webkit-datetime-edit-day-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-year-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-month-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-day-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-hour-field:focus, input[type="datetime-local"]::-webkit-datetime-edit-minute-field:focus':
//           {
//             backgroundColor: colors.primary.main,
//             color: '#ffffff',
//             borderRadius: '2px',
//             outline: 'none',
//           },

//         // ── FIX 2b: Date picker calendar icon color in dark mode ─────────────
//         '.MuiInputAdornment-root .MuiIconButton-root': {
//           color: `${colors.neutral.gray[500]} !important`,
//         },
//         '.MuiInputAdornment-root .MuiIconButton-root:hover': {
//           color: `${colors.primary.main} !important`,
//           backgroundColor: `${colors.primary.main}14 !important`,
//         },
//         // Date/Time picker dialog & desktop popper (MUI X DatePicker)
//         '.MuiPickersPopper-paper, .MuiDateCalendar-root, .MuiPickersLayout-root': {
//           backgroundColor: `${colors.background.paper} !important`,
//           backgroundImage: 'none !important',
//           color: `${colors.neutral.gray[800]} !important`,
//         },
//         '.MuiPickersDay-root': {
//           color: `${colors.neutral.gray[800]} !important`,
//           backgroundColor: 'transparent !important',
//         },
//         '.MuiPickersDay-root:hover': {
//           backgroundColor: `${colors.neutral.gray[100]} !important`,
//         },
//         '.MuiPickersDay-root.Mui-selected': {
//           backgroundColor: `${colors.primary.main} !important`,
//           color: '#ffffff !important',
//         },
//         '.MuiDayCalendar-weekDayLabel': {
//           color: `${colors.neutral.gray[500]} !important`,
//         },
//         '.MuiPickersCalendarHeader-label, .MuiPickersCalendarHeader-switchViewButton': {
//           color: `${colors.neutral.gray[800]} !important`,
//         },
//         '.MuiPickersArrowSwitcher-button': {
//           color: `${colors.neutral.gray[500]} !important`,
//         },
//         '.MuiPickersArrowSwitcher-button:hover': {
//           color: `${colors.primary.main} !important`,
//         },
//       }}
//     />
//   );

//   const theme = createTheme({
//     palette: {
//       mode: isDark ? 'dark' : 'light',
//       primary: {
//         main: colors.primary.main,
//         light: colors.primary.light,
//         dark: colors.primary.dark,
//         contrastText: '#ffffff',
//       },
//       secondary: {
//         main: colors.secondary.main,
//         light: colors.secondary.light,
//         dark: colors.secondary.dark,
//         contrastText: '#ffffff',
//       },
//       background: {
//         default: colors.background.default,
//         paper: colors.background.paper,
//       },
//       text: {
//         primary: colors.neutral.gray[800],
//         secondary: colors.neutral.gray[500],
//       },
//       grey: colors.neutral.gray as any,
//       success: { main: colors.status.success },
//       warning: { main: colors.status.warning },
//       error: { main: colors.status.error },
//       info: { main: colors.status.info },
//     },

//     typography: {
//       fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//       h1: { fontWeight: 700 },
//       h2: { fontWeight: 700 },
//       h3: { fontWeight: 600 },
//       h4: { fontWeight: 600 },
//       h5: { fontWeight: 600 },
//       h6: { fontWeight: 600 },
//       body1: { fontSize: '0.95rem', lineHeight: 1.6 },
//       body2: { fontSize: '0.875rem', lineHeight: 1.5 },
//     },

//     shape: {
//       borderRadius: parseInt(BORDER_RADIUS.md),
//     },

//     components: {
//       MuiButton: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.md,
//             textTransform: 'none',
//             fontWeight: 600,
//             fontSize: '0.95rem',
//             padding: '10px 24px',
//             boxShadow: 'none',
//             transition: 'all 0.2s ease-in-out',
//             '&:hover': {
//               boxShadow: shadows.primary,
//               transform: 'translateY(-1px)',
//             },
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//           },
//           contained: {
//             backgroundColor: colors.primary.main,
//             color: THEME_COLORS.neutral.white,
//             '&:hover': { backgroundColor: colors.primary.dark },
//             '&:disabled': {
//               background: colors.neutral.gray[300],
//               color: colors.neutral.gray[500],
//             },
//           },
//           outlined: {
//             borderColor: colors.neutral.gray[200],
//             color: colors.neutral.gray[600],
//             backgroundColor: colors.background.paper,
//             '&:hover': {
//               backgroundColor: colors.neutral.gray[50],
//               borderColor: colors.primary.main,
//             },
//           },
//         },
//       },

//       MuiCard: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.lg,
//             border: `1px solid ${colors.neutral.gray[100]}`,
//             boxShadow: shadows.md,
//             backgroundColor: colors.background.paper,
//             transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
//             '&:hover': {
//               boxShadow: shadows.lg,
//               transform: 'translateY(-2px)',
//             },
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//           },
//         },
//       },

//       MuiPaper: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.md,
//             boxShadow: shadows.md,
//             backgroundColor: colors.background.paper,
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//           },
//           elevation0: {
//             boxShadow: 'none',
//             border: `1px solid ${colors.neutral.gray[200]}`,
//           },
//           elevation1: { boxShadow: shadows.sm },
//           elevation3: { boxShadow: shadows.md },
//           elevation8: { boxShadow: shadows.xl },
//         },
//       },

//       MuiTextField: {
//         styleOverrides: {
//           root: {
//             '& .MuiOutlinedInput-root': {
//               borderRadius: BORDER_RADIUS.sm,
//               backgroundColor: colors.background.paper,
//               transition: 'all 0.2s ease-in-out',
//               '& fieldset': { borderColor: colors.neutral.gray[200] },
//               '&:hover fieldset': { borderColor: colors.primary.main },
//               '&.Mui-focused fieldset': {
//                 borderColor: colors.primary.main,
//                 borderWidth: 2,
//               },
//               '&:focus': { outline: 'none' },
//             },
//             '& .MuiOutlinedInput-root.Mui-focused': { outline: 'none' },
//             '& input:focus': { outline: 'none' },

//             // ── Native date/time input inside MUI TextField ──────────────────
//             // Ensure the color-scheme propagates even when the input is nested
//             // inside MUI's OutlinedInput wrapper.
//             '& input[type="date"], & input[type="datetime-local"], & input[type="time"], & input[type="month"], & input[type="week"]':
//               {
//                 colorScheme: isDark ? 'dark' : 'light',
//                 color: colors.neutral.gray[800],
//               },

//             // ── Custom SVG calendar icon (replaces OS-drawn icon via appearance:none)
//             '& input[type="date"]::-webkit-calendar-picker-indicator, & input[type="datetime-local"]::-webkit-calendar-picker-indicator, & input[type="time"]::-webkit-calendar-picker-indicator, & input[type="month"]::-webkit-calendar-picker-indicator, & input[type="week"]::-webkit-calendar-picker-indicator':
//               {
//                 WebkitAppearance: 'none',
//                 appearance: 'none',
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
//                 backgroundRepeat: 'no-repeat',
//                 backgroundPosition: 'center',
//                 backgroundSize: '16px 16px',
//                 width: '20px',
//                 height: '20px',
//                 cursor: 'pointer',
//                 borderRadius: '4px',
//                 padding: '2px',
//                 opacity: 0.85,
//                 transition: 'opacity 0.2s ease, background-color 0.2s ease',
//               },
//             '& input[type="date"]::-webkit-calendar-picker-indicator:hover, & input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover, & input[type="time"]::-webkit-calendar-picker-indicator:hover, & input[type="month"]::-webkit-calendar-picker-indicator:hover, & input[type="week"]::-webkit-calendar-picker-indicator:hover':
//               {
//                 opacity: 1,
//                 backgroundColor: `${colors.primary.main}20`,
//               },
//             // Focused date segments styled to primary colour
//             '& input[type="date"]::-webkit-datetime-edit-year-field:focus, & input[type="date"]::-webkit-datetime-edit-month-field:focus, & input[type="date"]::-webkit-datetime-edit-day-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-year-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-month-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-day-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-hour-field:focus, & input[type="datetime-local"]::-webkit-datetime-edit-minute-field:focus':
//               {
//                 backgroundColor: colors.primary.main,
//                 color: '#ffffff',
//                 borderRadius: '2px',
//                 outline: 'none',
//               },
//           },
//         },
//       },

//       MuiInputBase: {
//         styleOverrides: {
//           root: {
//             // ── FIX 3: Number input focus loss ─────────────────────────────
//             transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
//             '&:focus': { outline: 'none' },
//             '&.Mui-focused': { outline: 'none' },
//           },
//           input: {
//             transition: 'none',
//             '&:focus': { outline: 'none' },

//             // ── Native date input color-scheme on MuiInputBase level ─────────
//             // Catches cases where type="date" inputs are rendered directly
//             // inside InputBase without going through MuiTextField.
//             '&[type="date"], &[type="datetime-local"], &[type="time"], &[type="month"], &[type="week"]':
//               {
//                 colorScheme: isDark ? 'dark' : 'light',
//                 color: colors.neutral.gray[800],
//               },
//             '&[type="date"]::-webkit-calendar-picker-indicator, &[type="datetime-local"]::-webkit-calendar-picker-indicator, &[type="time"]::-webkit-calendar-picker-indicator, &[type="month"]::-webkit-calendar-picker-indicator, &[type="week"]::-webkit-calendar-picker-indicator':
//               {
//                 WebkitAppearance: 'none',
//                 appearance: 'none',
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
//                 backgroundRepeat: 'no-repeat',
//                 backgroundPosition: 'center',
//                 backgroundSize: '16px 16px',
//                 width: '20px',
//                 height: '20px',
//                 cursor: 'pointer',
//                 borderRadius: '4px',
//                 padding: '2px',
//                 opacity: 0.85,
//                 transition: 'opacity 0.2s ease, background-color 0.2s ease',
//               },
//           },
//         },
//       },

//       MuiOutlinedInput: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.sm,
//             backgroundColor: colors.background.paper,
//             backgroundImage: 'none',
//             transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
//             '&:focus': { outline: 'none' },
//             '&.Mui-focused': {
//               outline: 'none',
//               '& .MuiOutlinedInput-notchedOutline': {
//                 borderColor: colors.primary.main,
//                 borderWidth: 2,
//               },
//             },
//             '&:hover .MuiOutlinedInput-notchedOutline': {
//               borderColor: colors.primary.main,
//             },
//           },
//           notchedOutline: {
//             borderColor: colors.neutral.gray[200],
//           },
//           input: {
//             backgroundColor: 'transparent',
//             backgroundImage: 'none',
//             transition: 'none',
//             '&:focus': { outline: 'none' },

//             // ── Native date input color-scheme on OutlinedInput level ────────
//             '&[type="date"], &[type="datetime-local"], &[type="time"], &[type="month"], &[type="week"]':
//               {
//                 colorScheme: isDark ? 'dark' : 'light',
//                 color: colors.neutral.gray[800],
//               },
//             '&[type="date"]::-webkit-calendar-picker-indicator, &[type="datetime-local"]::-webkit-calendar-picker-indicator, &[type="time"]::-webkit-calendar-picker-indicator, &[type="month"]::-webkit-calendar-picker-indicator, &[type="week"]::-webkit-calendar-picker-indicator':
//               {
//                 WebkitAppearance: 'none',
//                 appearance: 'none',
//                 backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(colors.primary.main)}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='18' rx='2' ry='2'%3E%3C/rect%3E%3Cline x1='16' y1='2' x2='16' y2='6'%3E%3C/line%3E%3Cline x1='8' y1='2' x2='8' y2='6'%3E%3C/line%3E%3Cline x1='3' y1='10' x2='21' y2='10'%3E%3C/line%3E%3C/svg%3E")`,
//                 backgroundRepeat: 'no-repeat',
//                 backgroundPosition: 'center',
//                 backgroundSize: '16px 16px',
//                 width: '20px',
//                 height: '20px',
//                 cursor: 'pointer',
//                 borderRadius: '4px',
//                 padding: '2px',
//                 opacity: 0.85,
//                 transition: 'opacity 0.2s ease, background-color 0.2s ease',
//               },
//           },
//         },
//       },

//       MuiSelect: {
//         styleOverrides: {
//           root: {
//             backgroundColor: 'transparent',
//             '&:focus': { outline: 'none' },
//           },
//           select: {
//             backgroundColor: 'transparent !important',
//             '&:focus': {
//               backgroundColor: 'transparent',
//               outline: 'none',
//             },
//           },
//           icon: {
//             color: colors.neutral.gray[500],
//           },
//         },
//       },

//       MuiFormControl: {
//         styleOverrides: {
//           root: {
//             backgroundColor: 'transparent',
//           },
//         },
//       },

//       MuiInputLabel: {
//         styleOverrides: {
//           root: {
//             color: colors.neutral.gray[500],
//             '&.Mui-focused': {
//               color: colors.primary.main,
//             },
//           },
//         },
//       },

//       MuiInputAdornment: {
//         styleOverrides: {
//           root: {
//             color: colors.neutral.gray[500],
//             '& .MuiSvgIcon-root': {
//               color: colors.neutral.gray[500],
//             },
//             '& .MuiIconButton-root': {
//               color: colors.neutral.gray[500],
//               '&:hover': {
//                 color: colors.primary.main,
//                 backgroundColor: `${colors.primary.main}14`,
//               },
//             },
//           },
//         },
//       },

//       MuiMenu: {
//         styleOverrides: {
//           paper: {
//             backgroundColor: colors.background.paper,
//             backgroundImage: 'none',
//             border: `1px solid ${colors.neutral.gray[200]}`,
//             boxShadow: shadows.lg,
//             borderRadius: BORDER_RADIUS.sm,
//             // ── FIX: Dark mode scrollbar for dropdown menus ──────────────────
//             scrollbarWidth: 'thin',
//             scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//             '&::-webkit-scrollbar': {
//               width: '8px',
//               height: '8px',
//             },
//             '&::-webkit-scrollbar-track': {
//               background: colors.background.paper,
//               borderRadius: '4px',
//             },
//             '&::-webkit-scrollbar-thumb': {
//               backgroundColor: colors.neutral.gray[400],
//               borderRadius: '4px',
//               border: `2px solid ${colors.background.paper}`,
//             },
//             '&::-webkit-scrollbar-thumb:hover': {
//               backgroundColor: colors.neutral.gray[500],
//             },
//             '&::-webkit-scrollbar-corner': {
//               background: colors.background.paper,
//             },
//           },
//           list: {
//             padding: '4px',
//             // ── FIX: Also style scrollbar on the list itself if it overflows ─
//             scrollbarWidth: 'thin',
//             scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//             '&::-webkit-scrollbar': {
//               width: '8px',
//               height: '8px',
//             },
//             '&::-webkit-scrollbar-track': {
//               background: colors.background.paper,
//               borderRadius: '4px',
//             },
//             '&::-webkit-scrollbar-thumb': {
//               backgroundColor: colors.neutral.gray[400],
//               borderRadius: '4px',
//               border: `2px solid ${colors.background.paper}`,
//             },
//             '&::-webkit-scrollbar-thumb:hover': {
//               backgroundColor: colors.neutral.gray[500],
//             },
//           },
//         },
//       },

//       MuiMenuItem: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.sm,
//             margin: '2px 0',
//             color: colors.neutral.gray[800],
//             fontSize: '0.9rem',
//             transition: 'background-color 0.15s ease',
//             '&:focus': {
//               outline: 'none',
//               backgroundColor: colors.neutral.gray[50],
//             },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '-2px',
//             },
//             '&:hover': {
//               backgroundColor: colors.neutral.gray[100],
//             },
//             '&.Mui-selected': {
//               backgroundColor: `${colors.primary.main}20`,
//               color: colors.primary.main,
//               fontWeight: 600,
//               '&:hover': {
//                 backgroundColor: `${colors.primary.main}35`,
//               },
//             },
//           },
//         },
//       },

//       MuiTableContainer: {
//         styleOverrides: {
//           root: {
//             scrollbarWidth: 'thin',
//             scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//             '&::-webkit-scrollbar': {
//               width: '8px',
//               height: '8px',
//             },
//             '&::-webkit-scrollbar-track': {
//               background: colors.background.paper,
//               borderRadius: '4px',
//             },
//             '&::-webkit-scrollbar-thumb': {
//               backgroundColor: colors.neutral.gray[400],
//               borderRadius: '4px',
//               border: `2px solid ${colors.background.paper}`,
//             },
//             '&::-webkit-scrollbar-thumb:hover': {
//               backgroundColor: colors.neutral.gray[500],
//             },
//             '&::-webkit-scrollbar-corner': {
//               background: colors.background.paper,
//             },
//           },
//         },
//       },

//       MuiChip: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.sm,
//             fontWeight: 600,
//             fontSize: '0.8rem',
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//           },
//           outlined: {
//             '&.MuiChip-colorWarning': {
//               backgroundColor: isDark ? 'rgba(255,167,38,0.22)'  : 'rgba(255,167,38,0.12)',
//               borderColor:     isDark ? 'rgba(255,167,38,0.55)'  : 'rgba(255,167,38,0.40)',
//               color:           isDark ? '#ffcc80'                 : '#b76e00',
//               '& .MuiChip-icon':  { color: isDark ? '#ffa726' : '#e68900' },
//               '& .MuiChip-label': { color: isDark ? '#ffcc80' : '#b76e00' },
//             },
//             '&.MuiChip-colorSuccess': {
//               backgroundColor: isDark ? 'rgba(102,187,106,0.22)' : 'rgba(102,187,106,0.15)',
//               borderColor:     isDark ? 'rgba(102,187,106,0.50)' : 'rgba(102,187,106,0.45)',
//               color:           isDark ? '#a5d6a7'                 : '#2e7d32',
//               '& .MuiChip-icon':  { color: isDark ? '#66bb6a' : '#43a047' },
//               '& .MuiChip-label': { color: isDark ? '#a5d6a7' : '#2e7d32' },
//             },
//             '&.MuiChip-colorError': {
//               backgroundColor: isDark ? 'rgba(239,83,80,0.22)'   : 'rgba(239,83,80,0.12)',
//               borderColor:     isDark ? 'rgba(239,83,80,0.50)'   : 'rgba(239,83,80,0.40)',
//               color:           isDark ? '#ef9a9a'                 : '#c62828',
//               '& .MuiChip-icon':  { color: isDark ? '#ef5350' : '#e53935' },
//               '& .MuiChip-label': { color: isDark ? '#ef9a9a' : '#c62828' },
//             },
//             '&.MuiChip-colorInfo': {
//               backgroundColor: isDark ? 'rgba(41,182,246,0.20)'  : 'rgba(41,182,246,0.12)',
//               borderColor:     isDark ? 'rgba(41,182,246,0.50)'  : 'rgba(41,182,246,0.40)',
//               color:           isDark ? '#81d4fa'                 : '#0277bd',
//               '& .MuiChip-icon':  { color: isDark ? '#29b6f6' : '#039be5' },
//               '& .MuiChip-label': { color: isDark ? '#81d4fa' : '#0277bd' },
//             },
//             '&.MuiChip-colorPrimary': {
//               backgroundColor: isDark ? 'rgba(33,150,243,0.22)'  : 'rgba(33,150,243,0.12)',
//               borderColor:     isDark ? 'rgba(33,150,243,0.50)'  : 'rgba(33,150,243,0.40)',
//               color:           isDark ? '#90caf9'                 : '#1565c0',
//               '& .MuiChip-icon':  { color: isDark ? '#42a5f5' : '#1976d2' },
//               '& .MuiChip-label': { color: isDark ? '#90caf9' : '#1565c0' },
//             },
//             '&.MuiChip-colorSecondary': {
//               backgroundColor: isDark ? 'rgba(171,71,188,0.22)'  : 'rgba(171,71,188,0.10)',
//               borderColor:     isDark ? 'rgba(171,71,188,0.50)'  : 'rgba(171,71,188,0.35)',
//               color:           isDark ? '#ce93d8'                 : '#6a1b9a',
//               '& .MuiChip-icon':  { color: isDark ? '#ab47bc' : '#8e24aa' },
//               '& .MuiChip-label': { color: isDark ? '#ce93d8' : '#6a1b9a' },
//             },
//           },
//           colorPrimary: {
//             backgroundColor: colors.primary.main,
//             color: '#ffffff',
//           },
//         },
//       },

//       MuiListItemButton: {
//         styleOverrides: {
//           root: {
//             borderRadius: BORDER_RADIUS.md,
//             margin: '2px 0',
//             transition: 'all 0.2s ease-in-out',
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//             '&.Mui-selected': {
//               backgroundColor: colors.primary.main,
//               color: THEME_COLORS.neutral.white,
//               '&:hover': { backgroundColor: colors.primary.dark },
//               '& .MuiListItemIcon-root': { color: THEME_COLORS.neutral.white },
//               '& .MuiListItemText-primary': { color: THEME_COLORS.neutral.white },
//             },
//             '&:hover': { backgroundColor: colors.neutral.gray[50] },
//           },
//         },
//       },

//       MuiIconButton: {
//         styleOverrides: {
//           root: {
//             color: colors.neutral.gray[500],
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//           },
//         },
//       },

//       MuiAppBar: {
//         styleOverrides: {
//           root: {
//             backgroundColor: colors.background.paper,
//             color: colors.neutral.gray[800],
//             boxShadow: shadows.sm,
//             borderBottom: `1px solid ${colors.neutral.gray[100]}`,
//           },
//         },
//       },

//       MuiDrawer: {
//         styleOverrides: {
//           paper: {
//             backgroundColor: colors.background.default,
//             backgroundImage: 'none',
//             borderRight: `1px solid ${colors.neutral.gray[100]}`,
//             boxShadow: shadows.lg,
//             scrollbarWidth: 'thin',
//             scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.default}`,
//             '&::-webkit-scrollbar': {
//               width: '8px',
//               height: '8px',
//             },
//             '&::-webkit-scrollbar-track': {
//               background: colors.background.default,
//               borderRadius: '4px',
//             },
//             '&::-webkit-scrollbar-thumb': {
//               backgroundColor: colors.neutral.gray[400],
//               borderRadius: '4px',
//               border: `2px solid ${colors.background.default}`,
//             },
//             '&::-webkit-scrollbar-thumb:hover': {
//               backgroundColor: colors.neutral.gray[500],
//             },
//             '&::-webkit-scrollbar-corner': {
//               background: colors.background.default,
//             },
//             '&:focus': { outline: 'none' },
//           },
//         },
//       },

//       // ── FIX: Dialog scrollbar dark mode support ───────────────────────────
//       // MuiDialog-paper is the modal container (may itself scroll on small
//       // screens). MuiDialogContent-root is the primary scrollable region.
//       // Both get the same paper-background scrollbar treatment so the thumb
//       // and track match the dialog surface colour in dark mode.
//       MuiDialog: {
//         styleOverrides: {
//           paper: {
//             backgroundColor: colors.background.paper,
//             backgroundImage: 'none',
//             scrollbarWidth: 'thin',
//             scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//             '&::-webkit-scrollbar': {
//               width: '8px',
//               height: '8px',
//             },
//             '&::-webkit-scrollbar-track': {
//               background: colors.background.paper,
//               borderRadius: '4px',
//             },
//             '&::-webkit-scrollbar-thumb': {
//               backgroundColor: colors.neutral.gray[400],
//               borderRadius: '4px',
//               border: `2px solid ${colors.background.paper}`,
//             },
//             '&::-webkit-scrollbar-thumb:hover': {
//               backgroundColor: colors.neutral.gray[500],
//             },
//             '&::-webkit-scrollbar-corner': {
//               background: colors.background.paper,
//             },
//           },
//         },
//       },

//       MuiDialogContent: {
//         styleOverrides: {
//           root: {
//             scrollbarWidth: 'thin',
//             scrollbarColor: `${colors.neutral.gray[400]} ${colors.background.paper}`,
//             '&::-webkit-scrollbar': {
//               width: '8px',
//               height: '8px',
//             },
//             '&::-webkit-scrollbar-track': {
//               background: colors.background.paper,
//               borderRadius: '4px',
//             },
//             '&::-webkit-scrollbar-thumb': {
//               backgroundColor: colors.neutral.gray[400],
//               borderRadius: '4px',
//               border: `2px solid ${colors.background.paper}`,
//             },
//             '&::-webkit-scrollbar-thumb:hover': {
//               backgroundColor: colors.neutral.gray[500],
//             },
//             '&::-webkit-scrollbar-corner': {
//               background: colors.background.paper,
//             },
//           },
//         },
//       },

//       MuiTableHead: {
//         styleOverrides: {
//           root: {
//             '& .MuiTableCell-root': {
//               backgroundColor: colors.primary.main,
//               color: THEME_COLORS.neutral.white,
//               fontWeight: 600,
//               fontSize: '0.9rem',
//             },
//           },
//         },
//       },

//       MuiTableRow: {
//         styleOverrides: {
//           root: {
//             '&:hover': { backgroundColor: colors.neutral.gray[50] },
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '-2px',
//             },
//           },
//         },
//       },

//       MuiAlert: {
//         styleOverrides: {
//           root: { borderRadius: BORDER_RADIUS.md },
//         },
//       },

//       MuiLinearProgress: {
//         styleOverrides: {
//           root: {
//             borderRadius: '4px',
//             height: 8,
//             backgroundColor: colors.neutral.gray[200],
//           },
//           bar: { backgroundColor: colors.primary.main },
//         },
//       },

//       MuiCheckbox: {
//         styleOverrides: {
//           root: {
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//               borderRadius: '4px',
//             },
//             '&.Mui-checked': { color: colors.primary.main },
//           },
//         },
//       },

//       MuiRadio: {
//         styleOverrides: {
//           root: {
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//               borderRadius: '50%',
//             },
//             '&.Mui-checked': { color: colors.primary.main },
//           },
//         },
//       },

//       MuiSwitch: {
//         styleOverrides: {
//           root: {
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//               borderRadius: BORDER_RADIUS.full,
//             },
//           },
//           switchBase: {
//             '&.Mui-checked': {
//               color: colors.primary.main,
//               '& + .MuiSwitch-track': {
//                 backgroundColor: colors.primary.main,
//               },
//             },
//           },
//         },
//       },

//       MuiSlider: {
//         styleOverrides: {
//           root: {
//             color: colors.primary.main,
//             '& .MuiSlider-thumb:focus': { outline: 'none' },
//             '& .MuiSlider-thumb:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//           },
//         },
//       },

//       MuiTab: {
//         styleOverrides: {
//           root: {
//             '&:focus': { outline: 'none' },
//             '&:focus-visible': {
//               outline: `2px solid ${colors.primary.main}`,
//               outlineOffset: '2px',
//             },
//             '&.Mui-selected': { color: colors.primary.main },
//           },
//         },
//       },

//       MuiTabs: {
//         styleOverrides: {
//           indicator: { backgroundColor: colors.primary.main },
//         },
//       },

//       MuiDivider: {
//         styleOverrides: {
//           root: {
//             borderColor: colors.neutral.gray[100],
//           },
//         },
//       },

//       MuiTooltip: {
//         styleOverrides: {
//           tooltip: {
//             backgroundColor: isDark ? colors.neutral.gray[200] : colors.neutral.gray[800],
//             color: isDark ? colors.neutral.gray[800] : colors.neutral.white,
//             fontSize: '0.75rem',
//             borderRadius: BORDER_RADIUS.sm,
//           },
//           arrow: {
//             color: isDark ? colors.neutral.gray[200] : colors.neutral.gray[800],
//           },
//         },
//       },
//     },
//   });

//   return (
//     <MuiThemeProvider theme={theme}>
//       <CssBaseline />
//       {globalStyles}
//       {children}
//     </MuiThemeProvider>
//   );
// };

// interface ThemeProviderProps {
//   children: React.ReactNode;
// }

// export const AppThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
//   return (
//     <ThemeModeProvider>
//       <ThemedApp>{children}</ThemedApp>
//     </ThemeModeProvider>
//   );
// };

// // Export theme for use in components (light version as default)
// export const appTheme = createTheme({
//   palette: {
//     primary: {
//       main: THEME_COLORS.primary.main,
//       light: THEME_COLORS.primary.light,
//       dark: THEME_COLORS.primary.dark,
//       contrastText: '#ffffff',
//     },
//   },
// });

// export default AppThemeProvider;